import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';
import { FirebaseError } from 'firebase/app';
import { onAuthStateChanged } from 'firebase/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  auth,
  createUserWithEmailAndPassword,
  googleProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from '@/lib/firebase';
import { syncUser } from '@/lib/api/auth';
import { ME_QUERY_KEY, getMe } from '@/lib/api/users';
import { useBusinessStore } from '@/stores/business-store';
import type { User } from '@/types';

// ─── Context types ────────────────────────────────────────────────────────────

interface AuthProviderState {
  logInWithEmail: (email: string, password: string) => Promise<string | null>;
  logInWithGoogle: (email: string) => Promise<string | null>;
  logOut: () => Promise<void>;
  sendResetEmail: (email: string) => Promise<string | null>;
  signUp: (email: string, password: string) => Promise<string | null>;
  completeOnboarding: () => Promise<void>;
  user: User | null;
}

const initialState: AuthProviderState = {
  logInWithEmail: async () => null,
  logInWithGoogle: async () => null,
  logOut: async () => {},
  sendResetEmail: async () => null,
  signUp: async () => null,
  completeOnboarding: async () => {},
  user: null,
};

const AuthContext = createContext<AuthProviderState>(initialState);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const [firebaseUid, setFirebaseUid] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Fetches the backend user row + business memberships. Enabled only once
  // we have a Firebase session that has been synced server-side (see the
  // onAuthStateChanged effect below). Cached under ME_QUERY_KEY so other
  // flows (e.g. onboarding completion) can invalidate/refetch it.
  const meQuery = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: getMe,
    enabled: Boolean(firebaseUid),
  });

  const logInWithEmail = useCallback(
    async (email: string, password: string) => {
      try {
        await signInWithEmailAndPassword(auth, email, password);
      } catch (error) {
        if (error instanceof FirebaseError) return error.code;
        return 'auth/unknown';
      }
      return null;
    },
    [],
  );

  const logInWithGoogle = useCallback(async (email: string) => {
    try {
      if (email) googleProvider.setCustomParameters({ login_hint: email });
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      if (error instanceof FirebaseError) return error.code;
      return 'auth/unknown';
    }
    return null;
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error) {
      if (error instanceof FirebaseError) return error.code;
      return 'auth/unknown';
    }
    return null;
  }, []);

  const logOut = useCallback(async () => {
    try {
      await signOut(auth);
      setFirebaseUid(null);
      queryClient.removeQueries({ queryKey: ME_QUERY_KEY });
    } catch {
      toast.error('Logout failed');
    }
  }, [queryClient]);

  const sendResetEmail = useCallback(async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email, {
        handleCodeInApp: false,
        url: window.location.origin,
      });
    } catch (error) {
      if (error instanceof FirebaseError) return error.code;
      return 'auth/unknown';
    }
    return null;
  }, []);

  /**
   * Called after the onboarding wizard has already made its real
   * `PATCH /users/me` and `POST /businesses` calls (see
   * `src/routes/onboarding/index.tsx`). `onboarded` is fully server-derived
   * (see `user` below — true once `businesses.length > 0`), so this just
   * refetches `/users/me` and waits for it, so the caller can safely
   * navigate once `user.businesses` reflects the new membership.
   */
  const completeOnboarding = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
  }, [queryClient]);

  // Build the app-level `User` from the live /users/me response — id, email,
  // name, and avatar all come from the backend row now, not the raw
  // Firebase SDK user object.
  const user: User | null = useMemo(() => {
    if (!firebaseUid || !meQuery.data) return null;
    const me = meQuery.data;
    return {
      avatar: me.avatar_url,
      businesses: me.businesses,
      email: me.email,
      id: me.id,
      name: me.full_name,
      onboarded: me.businesses.length > 0,
    };
  }, [firebaseUid, meQuery.data]);

  const loading = authLoading || (Boolean(firebaseUid) && meQuery.isPending);

  const value = useMemo(
    () => ({
      logInWithEmail,
      logInWithGoogle,
      logOut,
      sendResetEmail,
      signUp,
      completeOnboarding,
      user,
    }),
    [
      logInWithEmail,
      logInWithGoogle,
      logOut,
      sendResetEmail,
      signUp,
      completeOnboarding,
      user,
    ],
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        setFirebaseUid(null);
        queryClient.removeQueries({ queryKey: ME_QUERY_KEY });
        // Clear the persisted active-business selection so a different
        // account signing in on this browser doesn't inherit it.
        useBusinessStore.getState().setActiveBusinessId(null);
        setAuthLoading(false);
        return;
      }

      // Verify the Firebase token server-side and find-or-create the
      // backend `users` row before trusting any other backend state.
      // This runs on every sign-in/sign-up/Google popup, since
      // onAuthStateChanged fires after each of those succeeds.
      void (async () => {
        try {
          await syncUser();
        } catch {
          toast.error('We could not verify your account. Please sign in again.');
          setFirebaseUid(null);
          await signOut(auth);
          setAuthLoading(false);
          return;
        }

        setFirebaseUid(currentUser.uid);
        setAuthLoading(false);
      })();
    });

    return () => unsubscribe();
  }, [queryClient]);

  // /users/me failed (expired session, 401, 500, ...) — treat as logged out
  // rather than rendering with stale or absent user data.
  useEffect(() => {
    if (!meQuery.isError) return;
    toast.error('We could not load your account. Please sign in again.');
    setFirebaseUid(null);
    void signOut(auth);
  }, [meQuery.isError]);

  return <AuthContext value={value}>{loading ? null : children}</AuthContext>;
}

export function useUser() {
  const context = use(AuthContext);
  if (context === undefined) {
    throw new Error('useUser must be used within an AuthProvider');
  }
  return context;
}
